/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import PayoutSearch from "./components/PayoutSearch";
import PayoutList from "./components/PayoutList";
import PayoutToolbar from "./components/PayoutToolbar";
import { Box } from "@chakra-ui/react";
import moment from "moment";
import { usePageWebSocket } from "../../../hooks/usePageWebSocket";

interface Filters {
  [key: string]: any;
}

interface PayloadType {
  [key: string]: any;
}

const rowPerPage = 10;

const initFrom = moment().subtract(1, "month");
const initTo = moment();
const initialDateRangeFilter = [initFrom.toDate(), initTo.toDate()];

const initialFilters: Filters = {
  from: initFrom,
  to: initTo,
  field: "",
  keyword: "",
  merchant_id: "",
  subaccount_id: "",
  receiver_id: "",
  receiver_account_id: "",
  asset: "",
  status: "",
};

const Payout = ({ userPermissions }: { userPermissions: any }) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isFilterLoading, setIsFilterLoading] = useState<boolean>(true);
  const [tableData, setTableData] = useState([]);
  const [filters, setFilters] = useState(initialFilters);
  const [lastKeys, setLastKeys] = useState<{ [key: number]: string }>({});
  const [currentLastKey, setCurrentLastKey] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(rowPerPage);
  const [rowCount, setRowCount] = useState(rowPerPage);
  const [selectedDates, setSelectedDates] = useState<Date[]>(
    initialDateRangeFilter
  );
  const userRole = localStorage.getItem("userRole")?.toLowerCase();

  const [assetList, setAssetList] = useState<any>(null);

  // NEW: control filter toggle
  const [showFilters, setShowFilters] = useState(false);

  // Track batch IDs currently being processed — used to hide cancel buttons
  const [processingBatchIds, setProcessingBatchIds] = useState<Set<string>>(new Set());

  const { received } = usePageWebSocket("payout_list");

  // AbortController for cancelling requests on unmount
  const abortControllerRef = useRef<AbortController | null>(null);
  // Request ID to track the latest request and prevent stale updates
  const requestIdRef = useRef(0);

  // Cleanup: Cancel all pending requests when component unmounts
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Create new AbortController for each batch of requests
  const getAbortSignal = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    return abortControllerRef.current.signal;
  };

  useEffect(() => {
    const signal = getAbortSignal();
    // Increment request ID to invalidate any previous requests
    const currentRequestId = ++requestIdRef.current;

    const fetchData = async () => {
      localStorage.removeItem("merchantIdToFilterPayout");
      localStorage.removeItem("subaccountIdToFilterPayout");
      localStorage.removeItem("receiverIdToFilterPayout");
      localStorage.removeItem("receiverAccountIdToFilterPayout");

      await fetchPayouts({
        page: 0,
        limit: rowPerPage,
        lastkey: "",
        ...filters,
      }, false, signal, currentRequestId);
      if (signal.aborted || requestIdRef.current !== currentRequestId) return;
      if (userRole === "superadmin") {
        await fetchAssetsForSuperadmin(signal);
        if (signal.aborted || requestIdRef.current !== currentRequestId) return;
      } else if (userRole === "merchant") {
        await fetchAssetsForMerchant(signal);
        if (signal.aborted || requestIdRef.current !== currentRequestId) return;
      } else {
        await fetchAssets(signal);
        if (signal.aborted || requestIdRef.current !== currentRequestId) return;
      }

      setIsLoading(false);
      setIsFilterLoading(false);
    };

    fetchData();
  }, []);

  // Listen for WebSocket messages
  useEffect(() => {
    if (!received) return;

    // Handle individual payout cancel
    if (received.messageType === "payout") {
      const payoutId = received.data?.payout_id;
      const newStatus = received.data?.status;
      if (payoutId && newStatus) {
        setTableData((prev: any) => prev.map((row: any) =>
          row.id === payoutId ? { ...row, status: newStatus } : row
        ));
      }
      return;
    }

    if (received.messageType !== "batch") return;

    const batchId = received.data?.batch_id;
    if (!batchId) return;

    const isProcessingStarted = !!received.data?.processing;

    if (isProcessingStarted) {
      // Processing started — just track batch ID to hide cancel buttons, no refetch
      setProcessingBatchIds(prev => {
        const next = new Set(prev);
        next.add(batchId);
        return next;
      });
    } else {
      // SNS — refetch if batch payouts are on current page
      // Don't remove from processingBatchIds here — the fetched data will have
      // the updated batch_status which already blocks cancel via batchAllowsCancel
      if (tableData.some((row: any) => row.batch_id === batchId)) {
        fetchPayouts({
          page: page,
          limit: rowPerPage,
          lastkey: page !== 0 ? currentLastKey : "",
          ...filters,
        });
      }
    }
  }, [received]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPayouts = async (payload: PayloadType = {}, isReset = false, signal?: AbortSignal, requestId?: number) => {
    try {
      const formattedFromDate = moment(
        !isReset ? selectedDates[0] : initFrom,
        "DD/MM/YYYY"
      ).format("YYYY-MM-DD 00:00");
      const formattedToDate = moment(
        !isReset ? selectedDates[1] : initTo,
        "DD/MM/YYYY"
      ).format("YYYY-MM-DD 23:59");

      const queryObject: PayloadType = {
        limit: payload?.limit,
        lastkey: payload?.lastkey,
        from: formattedFromDate,
        to: formattedToDate,
        usertype: userRole === "superadmin" ? "a" : "m",
      };

      const merchantId = localStorage.getItem("merchantIdToFilterPayout");
      const subaccountId = localStorage.getItem("subaccountIdToFilterPayout");
      const receiverId = localStorage.getItem("receiverIdToFilterPayout");
      const receiverAccountId = localStorage.getItem(
        "receiverAccountIdToFilterPayout"
      );

      if (
        payload?.field !== "" &&
        payload?.field !== undefined &&
        payload?.keyword !== "" &&
        payload?.keyword !== undefined
      ) {
        queryObject[payload?.field] = payload?.keyword;
      }

      if (payload?.status !== "" && payload?.status !== undefined) {
        queryObject.status = payload?.status;
      }

      if (payload?.asset !== "" && payload?.asset !== undefined) {
        queryObject.asset = payload?.asset;
      }

      if (userRole === "superadmin") {
        if (merchantId && merchantId !== "" && merchantId !== undefined) {
          queryObject.merchant_id = merchantId;
        }
      }

      if (userRole === "superadmin" || userRole === "merchant") {
        if (subaccountId && subaccountId !== "" && subaccountId !== undefined) {
          queryObject.subaccount_id = subaccountId;
        }
      }

      if (receiverId && receiverId !== "" && receiverId !== undefined) {
        queryObject.receiver_id = receiverId;
      }

      if (
        receiverAccountId &&
        receiverAccountId !== "" &&
        receiverAccountId !== undefined
      ) {
        queryObject.receiver_account_id = receiverAccountId;
      }

      const queryString = Object.keys(queryObject)
        .map(
          (key) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(queryObject[key])}`
        )
        .join("&");

      const response = await axios.get(
        process.env.REACT_APP_API_URL +
          `/api/payout/get-all-payouts?${queryString}`,
        { signal }
      );

      // Don't update state if request was aborted or a newer request was started
      if (signal?.aborted) return;
      if (requestId !== undefined && requestIdRef.current !== requestId) return;

      let newData = response.data.payouts?.payload;

      if (newData && newData.length !== 0) {
        setTableData(newData);
      } else {
        setTableData([]);
      }

      setIsLoading(false);

      // Manage Last Key and Total Record
      let newLastKey = response.data.payouts?.lastkey;
      if (newLastKey) {
        setCurrentLastKey(newLastKey);
        let newPage = payload?.page;
        let newPageSize = payload?.limit;

        if (newData.length !== 0) {
          setRowCount((newPage + 1) * newPageSize + newPageSize);
        } else {
          setRowCount(newPage * newPageSize + newPageSize);
        }
        setLastKeys({ ...lastKeys, [newPage]: newLastKey });
      }
    } catch (error: any) {
      // Ignore cancelled requests
      if (error.name === "CanceledError" || error.name === "AbortError") {
        return;
      }
      // Don't update state if a newer request was started
      if (requestId !== undefined && requestIdRef.current !== requestId) return;
      setTableData([]);
      setIsLoading(false);
      // Clear all
      setPage(0);
      setPageSize(0);
      setRowCount(0);
      setLastKeys([]);
      setCurrentLastKey("");
      console.error("Error fetching data", error);
    }
  };

  const fetchAssetsForSuperadmin = async (signal?: AbortSignal) => {
    try {
      let requestUrl = `/api/datasource/assets`;

      const response = await axios.get(
        process.env.REACT_APP_API_URL + requestUrl,
        { signal }
      );

      const data = response?.data;

      setAssetList(data);
    } catch (error: any) {
      if (error.name === "CanceledError" || error.name === "AbortError") return;
      console.error("Error fetching assets", error);
    }
  };

  const fetchAssetsForMerchant = async (signal?: AbortSignal) => {
    const merchantId = localStorage.getItem("merchantId");
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/merchant/get-merchant-by-id?merchantid=${merchantId}`,
        { signal }
      );

      const accountData = response.data.merchant?.payload;

      if (!accountData) {
        throw new Error("No merchant data found");
      }

      const assetMap = new Map();

      accountData.account_methods.forEach((method: any) => {
        if (!assetMap.has(method.asset)) {
          assetMap.set(method.asset, {
            name: method.asset,
            crypto: method.crypto,
          });
        }
      });

      const uniqueAssets = Array.from(assetMap.values());
      setAssetList(uniqueAssets);
    } catch (error: any) {
      if (error.name === "CanceledError" || error.name === "AbortError") return;
      console.error("Error fetching merchant details:", error);
    }
  };

  const fetchAssets = async (signal?: AbortSignal) => {
    const subaccountId = localStorage.getItem("subaccountId");
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/subaccount/get-subaccount-by-id?subaccountid=${subaccountId}`,
        { signal }
      );

      const accountData = response.data.subaccount?.payload;

      if (!accountData) {
        throw new Error("No subaccount data found");
      }

      const assetMap = new Map();

      accountData.account_methods.forEach((method: any) => {
        if (!assetMap.has(method.asset)) {
          assetMap.set(method.asset, {
            name: method.asset,
            decimal: method.decimal ?? 2,
            crypto: method.crypto,
          });
        }
      });

      const uniqueAssets = Array.from(assetMap.values());
      setAssetList(uniqueAssets);
    } catch (error: any) {
      if (error.name === "CanceledError" || error.name === "AbortError") return;
      console.error("Error fetching subaccount details:", error);
    }
  };

  const setPaginationModel = (newValue: any) => {
    setIsLoading(true);

    let newFilters: any = {
      page: newValue.page,
      limit: newValue.pageSize,
      lastkey: currentLastKey,
      ...filters,
    };

    // If click prev pagination
    if (newValue.page < page) {
      newFilters = {
        ...newFilters,
        lastkey: newValue.page !== 0 ? lastKeys[newValue.page - 1] : "",
      };
    }

    // if select new row per page, reset page to 0, reset lastkey
    if (newValue.pageSize !== pageSize) {
      newFilters = {
        page: 0,
        limit: newValue.pageSize,
        lastkey: "",
        ...filters,
      };
      setPage(0);
      setPageSize(newValue.pageSize);
      setCurrentLastKey("");
      setLastKeys([]);
    } else {
      setPage(newValue.page);
    }

    fetchPayouts(newFilters);
  };

  const handleSearchSubmit = async () => {
    setIsLoading(true);
    setIsFilterLoading(true);
    setCurrentLastKey("");
    setLastKeys({});
    setPage(0);
    await fetchPayouts({ page: 0, limit: rowPerPage, lastkey: "", ...filters });
    setIsFilterLoading(false);
  };

  const handleSearchClear = async () => {
    localStorage.removeItem("merchantIdToFilterPayout");
    localStorage.removeItem("subaccountIdToFilterPayout");
    localStorage.removeItem("receiverIdToFilterPayout");
    localStorage.removeItem("receiverAccountIdToFilterPayout");

    setIsLoading(true);
    setIsFilterLoading(true);
    setFilters(initialFilters);
    setPage(0);
    setCurrentLastKey("");
    setLastKeys([]);
    setSelectedDates(initialDateRangeFilter);
    await fetchPayouts(
      { page: 0, limit: rowPerPage, lastkey: "", ...initialFilters },
      true
    );
    setIsFilterLoading(false);
  };

  const triggerReloadPayoutList = async () => {
    setIsLoading(true);
    setIsFilterLoading(true);
    await fetchPayouts({
      page: page,
      limit: rowPerPage,
      lastkey: page !== 0 ? currentLastKey : "",
      ...filters,
    });
    setIsFilterLoading(false);
  };

  return (
    <Box sx={{ position: "relative" }}>
      <PayoutToolbar
        isLoading={isLoading || isFilterLoading}
        onToggleFilters={() => setShowFilters((p) => !p)}
        onRefresh={triggerReloadPayoutList}
      />

      <PayoutSearch
        showFilters={showFilters}
        userRole={userRole}
        isFilterLoading={isFilterLoading}
        filters={filters}
        setFilters={setFilters}
        setSelectedDates={setSelectedDates}
        selectedDates={selectedDates}
        handleSearchSubmit={handleSearchSubmit}
        handleSearchClear={handleSearchClear}
        assetList={assetList}
      />

      <PayoutList
        userPermissions={userPermissions}
        isLoading={isLoading}
        page={page}
        pageSize={pageSize}
        rowCount={rowCount}
        tableData={isLoading ? [] : tableData}
        setPaginationModel={setPaginationModel}
        currentLastKey={currentLastKey}
        triggerReloadPayoutList={triggerReloadPayoutList}
        processingBatchIds={processingBatchIds}
      />
    </Box>
  );
};

export default Payout;
